import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid2 as Grid,
  MenuItem,
  TextField,
  Button,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconLock from "@mui/icons-material/LockOpen";
import { validate } from "validate.js";
import "./ReportIncident.css";
import { useDispatch, useSelector } from "react-redux";
import {
  resetStateIncidents,
  submitIncident,
} from "../../app/slices/incidentsSlice";
import AppMessage from "../../components/AppMessage/AppMessage";
import moment from "moment";
import { oracleDateFormat } from "../../app/utils";
import IncidentForm from "./IncidentForm";

const ReportIncident = ({ isOpen, handleClose }) => {
  const { status, data, error } = useSelector(
    ({ incidents }) => incidents.post
  );
  const { activeSession } = useSelector(({ auth }) => auth);

  const [response, setResponse] = useState(null);
  const [activeView, setActiveView] = useState(
    activeSession?.session_token ? "incidentForm" : "notLoggedIn"
  );

  const [formValues, setFormValues] = useState({
    contactEmail: activeSession?.email || null,
  });
  const [errors, setErrors] = useState([]);
  const dispatch = useDispatch();

  validate.validators.datetime.parse = function (value) {
    return new Date(value); // Converts input to Date object
  };

  validate.validators.datetime.format = function (value, options) {
    const date = new Date(value);
    return options.dateOnly
      ? date.toISOString().split("T")[0] // Format as YYYY-MM-DD
      : date.toISOString();
  };

  const schema = {
    incidentCategory: {
      presence: { allowEmpty: false },
    },
    incidentDate: {
      presence: { allowEmpty: false },
      date: {
        dateOnly: true,
        latest: new Date(), // Ensures date is not in the future
        message: "^Incident date cannot be in the future",
      },
    },
    incidentDescription: {
      presence: { allowEmpty: false },
    },
    incidentRating: {
      presence: { allowEmpty: false },
    },
    tlpStatus: {
      presence: { allowEmpty: false },
    },
    incidentStatus: {
      presence: { allowEmpty: false },
    },
    contactEmail: {
      presence: { allowEmpty: false },
      email: true,
    },
  };

  const schemaClosed = {
    causeOfIncident: {
      presence: { allowEmpty: false },
    },
    incidentLocation: {
      presence: { allowEmpty: false },
    },
    systemsImpacted: {
      presence: { allowEmpty: false },
    },
    sourceOfDetection: {
      presence: { allowEmpty: false },
    },
    measuresTaken: {
      presence: { allowEmpty: false },
    },
    lessonsLearned: {
      presence: { allowEmpty: false },
    },
  };

  //use effects
  useEffect(() => {
    dispatch(resetStateIncidents(["post"]));
    return () => {
      dispatch(resetStateIncidents(["post"]));
    };
  }, []);

  useEffect(() => {
    if (error) {
      setResponse({
        type: "error",
        message: error?.message || "An error occurred",
      });
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      setResponse({
        type: "success",
        message:
          "Incident submitted. You will be contacted via email by TZ-Fincert team",
      });
    }
  }, [data]);

  //methods
  const handleChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value;

    setFormValues({
      ...formValues,
      [inputName]: inputValue,
    });
  };

  const handleSubmit = () => {
    let _errors = validate(
      formValues,
      formValues?.incidentStatus === "Closed"
        ? { ...schema, ...schemaClosed }
        : schema
    );

    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      setErrors([]);
    }

    dispatch(resetStateIncidents());

    dispatch(
      submitIncident({
        user_id: activeSession?.user_id,
        category: formValues?.incidentCategory,
        incident_date: moment(formValues?.incidentDate)?.format(
          oracleDateFormat
        ),
        rating: formValues?.incidentRating,
        status: formValues?.incidentStatus,
        tlp_status: formValues?.tlpStatus,
        description: formValues?.incidentDescription,
        location: formValues?.incidentLocation,
        cause: formValues?.causeOfIncident,
        detection_source: JSON.stringify(formValues?.sourceOfDetection || []),
        financial_impact: formValues?.financialImpact,
        non_financial_impact: JSON.stringify(
          formValues?.nonFinancialImpact || []
        ),
        impacted_softwares: formValues?.systemsImpacted,
        measures_taken: formValues?.measuresTaken,
        lessons_learned: formValues?.lessonsLearned,
        involved_persons: formValues?.namesOfPersonsInvolved,
        contact_email: formValues?.contactEmail,
      })
    );
  };

  //builders
  const NotLoggedInView = () => {
    return (
      <div id="not-logged-in-view">
        <span>
          You are currently not logged in. You can still report an incident as
          an anonymous user.
        </span>
        <div className="mt-20 flex-row justify-content-center align-items-center">
          <div
            className="nli-button"
            onClick={() => {
              setActiveView("incidentForm");
            }}
          >
            Continue as anonymous
          </div>
          <div
            className="nli-button"
            onClick={() => {
              window.location.replace("/login");
            }}
          >
            <IconLock />
            Go to login
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth={"md"}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitle id="report-incident-header">REPORT AN INCIDENT</DialogTitle>
      <DialogContent>
        <DialogContentText className="report-incident-content">
          {activeView === "incidentForm" && (
            <IncidentForm
              formValues={formValues}
              errors={errors}
              handleChange={handleChange}
            />
          )}
          {activeView === "notLoggedIn" && NotLoggedInView()}
        </DialogContentText>
      </DialogContent>
      <DialogActions id="report-incident-actions">
        <div id="report-incident-feedback">
          <AppMessage
            onClose={() => {
              setResponse(null);
            }}
            isOpen={response !== null}
            message={response?.message}
            messageType={response?.type}
          />
        </div>

        {data === null && (
          <Button
            onClick={handleSubmit}
            style={{ minWidth: "unset" }}
            id="button-submit"
            disabled={status === "loading"}
            variant="contained"
            color="primary"
            size="medium"
          >
            {status === "loading" && (
              <CircularProgress
                size={20}
                color="white"
                style={{ marginRight: "5px" }}
              />
            )}
            SUBMIT
          </Button>
        )}
        <Button
          onClick={() => {
            handleClose();
          }}
          id="button-close"
          style={{ minWidth: "unset" }}
          disabled={false}
          variant="contained"
          color="error"
          size="medium"
        >
          <CloseIcon />
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportIncident;
